<template>
  <div class="home d-flex flex-column justify-space-between">
    <div class="home-border-bt">
      <div class="home__top-container container">
        <div
          class="home__top-container-title align-items-center d-flex justify-space-between"
          style="flex-wrap: wrap"
        >
          <div style="flex-grow: 0">
            <div class="home__title">{{ t('Dashboard') }}</div>
          </div>

          <div class="home__right-select d-flex">
            <period-picker
              :showCompare="false"
              class="data-picker-head mr-4 ml-4"
            />

            <status-picker
              :silent="true"
              class="mr-4 ml-4"
              style="flex-grow: 0 !important; align-self: center;"
            />

            <currency-picker class="mr-4 ml-4"/>

            <ui-button
              class="main-menu__apply ml-4 mr-0"
              :text="isParamsChanged ? t('Apply') : t('Refresh')"
              :icon="isParamsChanged ? 'fas fa-check' : 'fas fa-sync-alt'"
              :button-style="isParamsChanged ? 'accent' : 'outline-accent'"
              @click="handleRefreshApply"
            />
          </div>
        </div>

        <div>
          <div class="card-mini-dashboard-wrap">
            <div class="card-mini-wr" v-for="item in statisticsData" :key="item.name">
              <div class="card-mini-dashboard">
                <div
                  class="num-wrapper"
                  :class="miniDashboardClass(item.value, item.name)"
                >
                  <span
                    v-if="item.name === 'reports-roi'"
                    class="num"
                  >{{ numeral(item.value || 0).format('0.00')}}%</span>
                  <span
                    v-if="['reports-cost', 'reports-revenue'].includes(item.name)"
                    class="num"
                  >
                    {{currencyIcon}}
                    {{numeral(item.value || 0).format('0,0.00')}}
                  </span>
                  <span
                    v-if="item.name==='reports-profit'"
                    class="num"
                  >
                    {{currencyIcon}}
                    {{numeral(item.value || 0).format('0,0.00')}}
                  </span>
                  <span
                    v-if="['reports-visits', 'reports-clicks', 'reports-conversionconfirm'].includes(item.name)"
                    class="num"
                  >{{ numeral(item.value || 0).format('0,0')}}</span>
                </div>
                <span class="title-mini">{{item.title}}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="home__table-container container">
      <div class="sort-row">
        <h3 class="home__sort-row-statistics-title">{{ t('Statistics') }}</h3>
        <div class="sort-select-wr">
          <span class="home__sort-row-title">{{ t('Sort by') }}</span>
          <div class="home__sort-row-controls pr-8" style="align-items: center;">
            <sort-by class="home__sort-profit mr-4 ml-4"></sort-by>
            <direction class="home__sort-dir mr-4 ml-4"></direction>
          </div>
        </div>
      </div>

      <div class="card-dashboard-wrapper d-flex flex-wrap" style="margin: -12px -15px;">
        <div
          v-for="card in dashboardData"
          :key="card.value"
          class="card-dashboard-wr"
          style="padding: 12px 15px;"
        >
          <div class="panel">
            <div class="panel-content widget-table panel-table-top">
              <table
                class="table table-condensed table-top"
                :class="{
                  'table-top--loading': loadingState
                }"
              >
                <colgroup v-if="!loadingState && card.data.length">
                  <col width="15px">
                  <col>
                  <col width="150px">
                </colgroup>
                <thead>
                <tr>
                  <th class="text-left" colspan="2">{{card.title}}</th>
                  <th class="t-prof">{{ sortingTitle }}</th>
                </tr>
                </thead>
                <tbody v-if="!loadingState && card.data.length">
                <tr v-for="item in card.data" :key="item.DT_RowId">
                  <td class="profit-column">
                      <span
                        class="profit"
                        :class="profitClass(item)"
                        v-if="orderBy === 'reports-profit'"
                      ></span>
                  </td>
                  <td v-html="item[card.fieldName]"></td>
                  <td class="text-right">
                      <span class="number" :class="profitClass(item)">
                        <span v-if="isCurrencyOrderBy">{{currencyIcon}} </span>
                        <span
                          v-if="isCurrencyOrderBy"
                        >{{ numeral(item[orderBy] || 0).format('0,0.00')}}</span>
                        <span v-else>{{ numeral(item[orderBy] || 0).format('0,0')}}</span>
                      </span>
                  </td>
                </tr>
                </tbody>
                <tbody v-if="loadingState">
                  <tr>
                    <td></td>
                    <td class="loading-class">
                      <loading-circle class="home__loading-circle"></loading-circle>
                    </td>
                  </tr>
                </tbody>
                <tbody class="no-data-t" v-if="!card.data.length && !loadingState">
                <tr>
                   <td class="no-data-t__td" colspan="3">
                    <ui-icon size="36" :color="$colors.gray20" icon="fas fa-hdd"></ui-icon>
                    <div>{{ t('Nothing to display for this search criteria') }}</div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container pt-0">
      <highcharts
        v-if="chartReady"
        :key="route.path"
        :options="chartOptions"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import StatusPicker from '@/views/reports/pickers/status-picker.vue';
import CurrencyPicker from '@/views/dashboard/pickers/currency-picker.vue';
import PeriodPicker from '@/views/reports/pickers/period-picker.vue';
import SortBy from '@/views/dashboard/pickers/sort-by.vue';
import Direction from '@/views/dashboard/pickers/direction.vue';
import LoadingCircle from '@/components/loading-circle.vue';
import { useHomeDashboard } from '@/composables/useHomeDashboard';
import { useRoute } from 'vue-router';
import numeral from 'numeral';
import { useColors } from '@/composables';

const $colors = useColors();
const route = useRoute();

const {
  t,
  chartOptions,
  chartReady,
  dashboardData,
  statisticsData,
  currencyIcon,
  loadingState,
  orderBy,
  sortingTitle,
  isCurrencyOrderBy,
  isParamsChanged,
  profitClass,
  miniDashboardClass,
  handleRefreshApply,
} = useHomeDashboard();
</script>

<style lang="scss">
  @import '@/styles/pages/home';

  .home .container {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    padding-left: 16px;
    padding-right: 16px;
    box-sizing: border-box;

    &.pt-0 {
      padding-bottom: 16px;
    }

    @media all and (min-width: 960px) {
      max-width: 1185px;
    }
  }

  .home {
    .card-dashboard-wr {
      width: 100%;

      @media all and (min-width: 960px) {
        width: 50%;
      }
    }

    .card-mini-dashboard-wrap {
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
    }

    .num-wrapper {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 0 8px;
    }

    @media all and (max-width: 1100px) {
      .card-mini-dashboard-wrap {
        flex-flow: wrap;
        justify-content: center;
        flex-direction: column;
      }

      .card-mini-wr {
        padding: 0 16px;
        background-color: var(--color-white);
        box-shadow: 0 2px 4px 0 var(--color-black20);
        width: 100%;

        &:last-child {
          padding-right: 16px;
        }
      }

      .card-mini-wr + .card-mini-wr {
        .card-mini-dashboard  {
          border-top: 1px solid var(--color-gray10);
        }
      }

      .card-mini-dashboard {
        box-shadow: none;
        display: flex;
        justify-content: space-between;
        flex-direction: row-reverse;

        height: 32px;
        width: 100%;
        min-width: none;
      }

      .num-wrapper {
        font-weight: 600;
        color: var(--color-black);
      }

      .card-mini-dashboard {
        .num {
          font-size: 13px;
        }
      }

      .title-mini {
        font-size: 13px;
        color: var(--color-black);
      }
    }
  }
</style>
